import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import useDesktop from '../../hooks/useDesktop';
import './TabellaOrdinabile.css';

/**
 * Un elenco in due forme. Sopra i 768px è una tabella con colonne ordinabili, perché a schermo
 * largo si confrontano le righe; sotto è una pila di card, perché su un telefono una tabella a
 * otto colonne o si taglia o si scorre di lato.
 *
 * Le due forme leggono le stesse `colonne` e le stesse `righe`: è il motivo per cui esiste un
 * componente solo invece di una tabella e una lista che si rincorrono.
 *
 * colonne: [{ chiave, etichetta, valore(riga), rende?(riga), testo?, ordinabile?, etichettaLunga?, soloTabella? }]
 *   - `valore` serve all'ordinamento e alla cella; `rende` la disegna quando non è testo semplice
 *     (un link, per esempio).
 *   - `testo: true` ordina alfabeticamente invece che numericamente.
 *   - `etichettaLunga` è il nome per esteso, usato in tabella dove c'è spazio; `etichetta`
 *     resta l'abbreviazione, che serve alle card di un telefono: una colonna intitolata «M»
 *     non si capisce se non si è già capito il resto della pagina.
 *   - `soloTabella: true` tiene la colonna fuori dalla forma a card: serve a chi la mostra
 *     già altrove nella card, per esempio nel badge, e non vuole vederla due volte.
 *
 * ATTENZIONE, regola per chi la usa: la forma a card NON mostra le colonne con `testo: true`,
 * perché su un telefono finirebbero nella fila delle statistiche invece che nell'intestazione.
 * Ne segue che un link messo nel `rende` di una colonna testuale esiste SOLO su desktop: il nome
 * cliccabile di una riga va reso anche da `titoloCard`. Passare a `titoloCard` una stringa
 * semplice fa sparire il link da telefono.
 */
export default function TabellaOrdinabile({
  colonne,
  righe,
  chiaveRiga,
  ordineIniziale = null,
  titoloCard,
  sottotitoloCard,
  badgeCard,
  vuoto = 'Nessun dato disponibile',
}) {
  const desktop = useDesktop();
  const [ordine, setOrdine] = useState(ordineIniziale);

  const ordinate = useMemo(() => {
    if (!ordine) return righe;
    const col = colonne.find((c) => c.chiave === ordine.campo);
    if (!col) return righe;
    const segno = ordine.verso === 'asc' ? 1 : -1;
    return [...righe].sort((a, b) => {
      const va = col.valore(a);
      const vb = col.valore(b);
      if (col.testo) return segno * String(va ?? '').localeCompare(String(vb ?? ''), 'it');
      return segno * ((va ?? 0) - (vb ?? 0));
    });
  }, [righe, colonne, ordine]);

  const cambiaOrdine = (chiave) =>
    setOrdine((cur) =>
      cur && cur.campo === chiave
        ? { campo: chiave, verso: cur.verso === 'desc' ? 'asc' : 'desc' }
        : { campo: chiave, verso: 'desc' });

  if (!righe.length) return <div className="tab-ord-vuoto">{vuoto}</div>;

  if (desktop) {
    return (
      <div className="tab-ord-scroll">
        <table className="tab-ord">
          <thead>
            <tr>
              {colonne.map((c) => (
                <th
                  key={c.chiave}
                  scope="col"
                  className={ordine?.campo === c.chiave ? 'ordinata' : undefined}
                  onClick={c.ordinabile === false ? undefined : () => cambiaOrdine(c.chiave)}
                >
                  {c.etichettaLunga ?? c.etichetta}
                  {ordine?.campo === c.chiave && (
                    ordine.verso === 'desc'
                      ? <ChevronDown size={12} aria-hidden="true" />
                      : <ChevronUp size={12} aria-hidden="true" />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ordinate.map((r) => (
              <tr key={chiaveRiga(r)}>
                {colonne.map((c) => (
                  <td key={c.chiave} data-testid={`cella-${c.chiave}`}>
                    {c.rende ? c.rende(r) : c.valore(r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="tab-ord-card-lista">
      {ordinate.map((r) => (
        <article className="tab-ord-card" key={chiaveRiga(r)}>
          <header className="tab-ord-card-top">
            <span className="tab-ord-card-titolo" data-testid="riga-titolo">{titoloCard ? titoloCard(r) : null}</span>
            {badgeCard ? <span className="tab-ord-card-badge">{badgeCard(r)}</span> : null}
          </header>
          {sottotitoloCard ? <div className="tab-ord-card-sotto">{sottotitoloCard(r)}</div> : null}
          <dl className="tab-ord-card-stat">
            {colonne.filter((c) => !c.testo && !c.soloTabella).map((c) => (
              <div key={c.chiave}>
                <dt>{c.etichetta}</dt>
                <dd data-testid={`cella-${c.chiave}`}>{c.rende ? c.rende(r) : c.valore(r)}</dd>
              </div>
            ))}
          </dl>
        </article>
      ))}
    </div>
  );
}
